import { useCallback, useEffect, useMemo, useState } from 'react'

import { SURVEY_FEEDBACK_MAX, newClientEventId, readSurvey, sendSurveyUntilSaved, storeSurvey } from './probeService'

// The short survey on a study condition's Thank You overlay (StudySurvey), for Workspace:
// { submitted, refused, submit(rating, feedback) }. The survey is kept in this browser before
// it is sent and sent until the server confirms it (probeService), from here rather than from
// the overlay, so "Keep working on the exercise" closing the overlay does not stop it. A reload
// shows a kept survey as submitted, and sends it again while it is not confirmed.
//   submitted  the student submitted it, on this page or an earlier load
//   refused    the server refused it (400): the form comes back for another try
// enabled: a study condition's student; otherwise nothing is read or sent.
export const useStudySurvey = (sessionId, userId, { enabled = false } = {}) => {
  const owner = `${sessionId}:${userId}`
  const active = enabled && Boolean(sessionId) && Boolean(userId)
  const stored = useMemo(() => (active ? readSurvey(sessionId, userId) : null), [active, sessionId, userId])
  // The survey submitted on this page (or refused), which wins over the stored one.
  const [local, setLocal] = useState(null)
  const survey = local?.owner === owner ? local.survey : stored
  const pending = active && survey && !survey.saved ? survey : null

  useEffect(() => {
    if (!pending) return undefined
    let cancelled = false
    sendSurveyUntilSaved(sessionId, pending, { isCancelled: () => cancelled }).then((result) => {
      if (!cancelled && result === false) setLocal({ owner, survey: null, refused: true })
    })
    return () => {
      cancelled = true
    }
    // One send per survey: the stored copy marked saved does not change the one in memory.
  }, [pending, sessionId, owner])

  const submit = useCallback(
    (rating, feedback) => {
      if (!active) return
      const entry = {
        userId,
        rating,
        feedback: String(feedback || '').trim().slice(0, SURVEY_FEEDBACK_MAX),
        clientEventId: newClientEventId(),
        saved: false,
      }
      // Stored before it is sent, so a reload a moment later cannot lose it.
      storeSurvey(sessionId, entry)
      setLocal({ owner, survey: entry, refused: false })
    },
    [active, owner, sessionId, userId]
  )

  return { submitted: Boolean(survey), refused: local?.owner === owner && local.refused === true, submit }
}
